<template>
  <div
    class="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 md:p-6 relative"
  >
    <!-- Settings Button -->
    <button
      class="absolute top-[160px] right-[60px] z-50 p-3 bg-white/90 dark:bg-gray-700/90 backdrop-blur-xl rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 border border-gray-200/50 dark:border-gray-600/50"
      @click="openSetting = true"
    >
      <q-icon
        name="settings"
        class="text-gray-600 dark:text-gray-300 text-xl"
      />
    </button>

    <div class="max-w-7xl mx-auto">
      <q-card
        class="shadow-2xl rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
      >
        <div
          class="bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-700 dark:to-indigo-700 p-6 text-white flex items-center justify-between"
        >
          <div>
            <h1 class="text-2xl font-bold mb-1">车辆能力测试集合</h1>
            <p class="text-blue-100 text-sm">按平台分组的调试页面</p>
          </div>
          <button
            @click="handleBack"
            class="px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
          >
            <div class="flex items-center justify-center gap-2">
              <q-icon name="arrow_back" />
              <span>返回主页</span>
            </div>
          </button>
        </div>

        <div class="p-6 space-y-6">
          <!-- 一级标签：平台 -->
          <div class="platform-tabs-container">
            <q-tabs
              v-model="activeGroup"
              dense
              class="platform-tabs"
              active-color="primary"
              indicator-color="primary"
              inline-label
              narrow-indicator
              align="left"
            >
              <q-tab
                v-for="group in groups"
                :key="group.key"
                :name="group.key"
                :label="group.label"
                class="platform-tab"
                :class="{ 'platform-tab--active': activeGroup === group.key }"
              />
            </q-tabs>
          </div>

          <!-- 二级标签：具体测试页 -->
          <div class="sub-tabs-container">
            <q-tabs
              v-model="activeTab"
              dense
              class="sub-tabs"
              active-color="primary"
              indicator-color="primary"
              inline-label
              narrow-indicator
              align="left"
            >
              <q-route-tab
                v-for="tab in currentTabs"
                :key="tab.routeName"
                :name="tab.routeName"
                :label="tab.label"
                :to="{ name: tab.routeName }"
                class="sub-tab"
                :class="{ 'sub-tab--active': activeTab === tab.routeName }"
              />
            </q-tabs>
          </div>

          <div class="content-container">
            <router-view v-if="showView" />
          </div>
        </div>
      </q-card>
    </div>

    <Setting v-model="openSetting" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useQuasar } from "quasar";
import { isWeb } from "@/utils/capacitor";
import { useDomainControlStore } from "@/stores/domainControl";
import VConsole from "vconsole";
import Setting from "@/views/DomainControl/components/Setting.vue";

type TabItem = { label: string; routeName: string };
type GroupItem = { key: string; label: string; tabs: TabItem[] };

const router = useRouter();
const route = useRoute();
const $q = useQuasar();
const domainControlStore = useDomainControlStore();

const openSetting = ref(false);
const showView = ref(true);

// vConsole
const vConsole = ref<VConsole | null>(null);

const groups = ref<GroupItem[]>([
  {
    key: "ss3",
    label: "SS3",
    tabs: [
      { label: "CarPlugin 测试", routeName: "CarPluginTest" },
      { label: "VehicleProperty 测试", routeName: "VehiclePropertyTest" },
    ],
  },
  {
    key: "ss4",
    label: "SS4",
    tabs: [
      { label: "CarPropertyClient 测试", routeName: "CarPropertyClientTest" },
      { label: "CarDiag 测试", routeName: "CarDiagTest" },
      { label: "VehiclePropertyMapper 测试", routeName: "VehiclePropertyMapperTest" },
      { label: "信号探索", routeName: "SignalExplorer" },
    ],
  },
  {
    key: "vbs",
    label: "VBS",
    tabs: [{ label: "VBS 测试", routeName: "VBSTest" }],
  },
]);

const activeGroup = ref<string>("ss3");
const activeTab = ref<string>("");

const routeGroupMap = computed<Record<string, string>>(() => {
  const map: Record<string, string> = {};
  groups.value.forEach((group) => {
    group.tabs.forEach((tab) => {
      map[tab.routeName] = group.key;
    });
  });
  return map;
});

const currentTabs = computed<TabItem[]>(() => {
  const found = groups.value.find((g) => g.key === activeGroup.value);
  return found ? found.tabs : [];
});

const ensureRouteMatchGroup = (groupKey: string) => {
  const targetGroup = groups.value.find((g) => g.key === groupKey);
  if (!targetGroup || targetGroup.tabs.length === 0) return;

  const currentName = route.name as string | undefined;
  const belongsToGroup =
    currentName && targetGroup.tabs.some((tab) => tab.routeName === currentName);

  if (!belongsToGroup) {
    router.push({ name: targetGroup.tabs[0].routeName });
  }
};

watch(
  () => route.name,
  (name) => {
    const routeName = (name as string) || "";
    const groupKey = routeGroupMap.value[routeName];
    if (groupKey) {
      activeGroup.value = groupKey;
      activeTab.value = routeName;
    }
  },
  { immediate: true }
);

watch(
  () => activeGroup.value,
  (val) => {
    ensureRouteMatchGroup(val);
  }
);

// 返回主页
const handleBack = () => {
  router.push("/");
};

// screen size
const handleSetScreenSize = () => {
  if (isWeb) return;
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;

  // 根据不同的屏幕尺寸设置不同的基准宽度
  let width;
  let height = 807; // 基准高度保持不变

  // 适配不同宽度的屏幕
  if (screenWidth >= 2800 && screenWidth <= 2900) {
    // 针对 2880 × 1360 尺寸
    width = 2000;
    console.log("[LOG] 检测到大尺寸屏幕 2880 × 1360");
  } else if (screenWidth < 1900) {
    // 原有的 1886 × 1360 及更小尺寸
    width = 1291;
  } else {
    // 其他尺寸
    width = 1400;
  }

  const viewport = document.querySelector('meta[name="viewport"]');

  console.log("[LOG] screenWidth:", screenWidth);
  console.log("[LOG] screenHeight:", screenHeight);
  console.log("[LOG] baseWidth:", width);

  // 根据屏幕宽度计算缩放比例
  let scale;
  if (screenWidth >= 2800 && screenWidth <= 2900) {
    // 对于2880宽度的屏幕使用特定的缩放计算
    scale = Math.min(screenWidth / width, screenHeight / height);
  } else {
    // 原有的缩放计算逻辑
    scale =
      screenWidth < 1900
        ? Math.min(screenWidth / width)
        : Math.min(screenWidth / width, screenHeight / height);
  }
  console.log("[BOWEN_LOG] 🚀 ~~ onMounted ~~ scale:", scale);
  viewport?.setAttribute(
    "content",
    "width=device-width, initial-scale=" + scale
  );
};

// theme
function applyTheme(theme: string) {
  $q.dark.set(theme === "dark");
}

const handleThemeChange = (e: any) => {
  applyTheme(e.matches ? "dark" : "light");
};

const watchThemeChange = () => {
  // 监听系统主题
  const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  applyTheme(mediaQuery.matches ? "dark" : "light");
  mediaQuery.addEventListener("change", handleThemeChange);
};

const handleResetView = () => {
  showView.value = false;
  setTimeout(() => {
    showView.value = true;
  }, 100);
};

const setupVConsole = () => {
  vConsole.value = new VConsole();
};

onMounted(() => {
  handleSetScreenSize();
  watchThemeChange();
});

watch(
  () => $q.dark.isActive,
  (val) => {
    console.log(val ? "On dark mode" : "On light mode");
    handleResetView();
    if (val) {
      document.body.classList.add("dark");
    } else {
      document.body.classList.remove("dark");
    }
    // set prefers-color-scheme
    if (val) {
      document.documentElement.setAttribute("data-theme", "dark");
    } else {
      document.documentElement.setAttribute("data-theme", "light");
    }
  }
);

watch(
  () => domainControlStore.showVConsole,
  (val) => {
    console.log("[BOWEN_LOG] 🚀 ~~ val:", val);
    if (val) {
      if (vConsole.value) {
        vConsole.value.showSwitch();
      } else {
        setupVConsole();
      }
    } else {
      vConsole.value?.hideSwitch();
    }
  },
  { immediate: true }
);
</script>

<style>
#__vconsole {
  font-size: 14px !important;
}
</style>

<style scoped>
/* 一级标签容器样式 */
.platform-tabs-container {
  background: linear-gradient(to right, rgb(239 246 255), rgb(224 231 255));
  border-radius: 0.75rem;
  padding: 0.5rem;
  border: 1px solid rgb(191 219 254);
}

.dark .platform-tabs-container {
  background: linear-gradient(to right, rgb(31 41 55), rgb(55 65 81));
  border-color: rgb(75 85 99);
}

/* 一级标签样式 */
.platform-tabs {
  color: rgb(31 41 55);
}

.dark .platform-tabs {
  color: rgb(243 244 246);
}

.platform-tab {
  padding: 0.5rem 1.5rem;
  margin: 0 0.25rem;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
  color: rgb(75 85 99);
  font-weight: 500;
}

.dark .platform-tab {
  color: rgb(156 163 175);
}

.platform-tab:hover {
  color: rgb(37 99 235);
  background-color: rgba(255, 255, 255, 0.5);
}

.dark .platform-tab:hover {
  color: rgb(96 165 250);
  background-color: rgba(55, 65, 81, 0.5);
}

.platform-tab--active {
  background-color: white;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
  color: rgb(37 99 235);
  font-weight: 600;
}

.dark .platform-tab--active {
  background-color: rgb(31 41 55);
  color: rgb(96 165 250);
}

/* 二级标签容器样式 */
.sub-tabs-container {
  background: linear-gradient(to right, rgb(250 245 255), rgb(253 244 255));
  border-radius: 0.75rem;
  padding: 0.5rem;
  border: 1px solid rgb(233 213 255);
}

.dark .sub-tabs-container {
  background: linear-gradient(to right, rgb(31 41 55), rgb(55 65 81));
  border-color: rgb(75 85 99);
}

/* 二级标签样式 */
.sub-tabs {
  color: rgb(31 41 55);
}

.dark .sub-tabs {
  color: rgb(243 244 246);
}

.sub-tab {
  padding: 0.5rem 1.25rem;
  margin: 0 0.25rem;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
  color: rgb(75 85 99);
  font-weight: 500;
}

.dark .sub-tab {
  color: rgb(156 163 175);
}

.sub-tab:hover {
  color: rgb(147 51 234);
  background-color: rgba(255, 255, 255, 0.5);
}

.dark .sub-tab:hover {
  color: rgb(196 181 253);
  background-color: rgba(55, 65, 81, 0.5);
}

.sub-tab--active {
  background-color: white;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
  color: rgb(147 51 234);
  font-weight: 600;
}

.dark .sub-tab--active {
  background-color: rgb(31 41 55);
  color: rgb(196 181 253);
}

/* 内容容器 */
.content-container {
  border-radius: 0.75rem;
  border: 1px solid rgb(229 231 235);
  background-color: white;
  overflow: hidden;
}

.dark .content-container {
  border-color: rgb(55 65 81);
  background-color: rgb(17 24 39);
}
</style>

